import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { GetTaskStatusRep } from '../task-repository';
import { TaskStatus } from '../../enums';
import type { TaskStatusSyncService } from '../task-status-sync-service';
import type { TaskItem } from '../../types';

export const STATUS_SYNC_DURATION = 1000;

@injectable()
export class TaskStatusSyncServiceImpl implements TaskStatusSyncService {
    private _onTaskStatusChangeEmitter = new Emitter<TaskItem[]>();
    public readonly onTaskStatusChange = this._onTaskStatusChangeEmitter.event;

    private _toBeSyncTasks: TaskItem[] = [];
    private _timer: NodeJS.Timeout | null = null;

    constructor(
        @inject(GetTaskStatusRep)
        private _getTaskStatusRep: GetTaskStatusRep,
    ) {
    }

    /**
     * 创建定时器
     * 定期触发任务状态同步
     */
    private async _createTimer() {
        this._timer = setTimeout(() => {
            this._syncTaskStatus();
        }, STATUS_SYNC_DURATION);
    }

    /**
     * 清除定时器
     * 当没有需要同步的任务时调用
     */
    private _clearTimer() {
        if (!this._timer) return;
        clearTimeout(this._timer);
        this._timer = null;
    }

    /**
     * 查找待同步任务列表中指定ID的任务
     * @param id 任务ID
     * @returns 找到的任务或undefined
     */
    private _findTaskInToBeSync(id: string) {
        return this._toBeSyncTasks.find(task => task.id === id);
    }

    /**
     * 从更新后的任务列表中筛选出状态发生变化的任务
     * @param updatedTasks 更新后的任务列表
     * @returns 状态发生变化的任务列表
     */
    private _getStatusChangedTasks(updatedTasks: Pick<TaskItem, 'id' | 'status'>[]) {
        const statusChangedTasks: TaskItem[] = [];

        updatedTasks.forEach(task => {
            const matchedTask = this._findTaskInToBeSync(task.id);

            if (!matchedTask) return;

            if (matchedTask.status !== task.status) {
                statusChangedTasks.push({
                    ...matchedTask,
                    status: task.status,
                });
            }
        });

        return statusChangedTasks;
    }

    /**
     * 清理已完成的任务
     * @param tasks 需要检查的任务列表
     */
    private _clearCompletedTask(tasks: TaskItem[]) {
        const completedTaskIds = tasks
            .filter(task => task.status === TaskStatus.COMPLETED)
            .map(task => task.id);

        this._toBeSyncTasks = this._toBeSyncTasks.filter(task => !completedTaskIds.includes(task.id));

        if (!this._toBeSyncTasks.length) this._clearTimer();
    }

    /**
     * 同步任务状态
     * 从服务器获取最新任务状态，并处理状态变更
     */
    private async _syncTaskStatus() {
        const toBeSyncTaskIds = this._toBeSyncTasks.map(task => task.id);

        const syncResult = await this._getTaskStatusRep.fetch(toBeSyncTaskIds);

        const statusChangedTasks = this._getStatusChangedTasks(syncResult);

        if (statusChangedTasks.length) {
            this._updateToBeSyncTasks(statusChangedTasks);
            this._clearCompletedTask(statusChangedTasks);
            this._onTaskStatusChangeEmitter.fire(statusChangedTasks);
        }

        if (this._toBeSyncTasks.length) {
            this._createTimer();
        }
    }

    /**
     * 将新任务添加到待同步任务列表
     * @param tasks 要添加的任务列表
     */
    private _pushToBeSyncTasks(tasks: TaskItem[]) {
        tasks.forEach(task => {
            const matchedTask = this._findTaskInToBeSync(task.id);

            if (!matchedTask) this._toBeSyncTasks.push(task);
        });
    }

    /**
     * 更新待同步任务的状态
     * @param tasks 包含新状态的任务列表
     */
    private _updateToBeSyncTasks(tasks: TaskItem[]) {
        tasks.forEach(task => {
            const matchedTask = this._findTaskInToBeSync(task.id);

            if (matchedTask) {
                matchedTask.status = task.status;
            }
        });
    }

    /**
     * 扫描任务状态
     * 将未完成的任务添加到待同步列表，并启动定时同步
     * @param data 要扫描的任务列表
     */
    public async scanTaskStatus(data: TaskItem[]) {
        const toBeSyncTasks = data.filter(task => task.status !== TaskStatus.COMPLETED);

        this._pushToBeSyncTasks(toBeSyncTasks);

        if (!this._timer && this._toBeSyncTasks.length) this._createTimer();
    }
}
